<template>
  <div class="seller-products-page market-project market-project-v2">
    <div class="seller-products-page__layout">
      <div class="accFeed__wrapper matchmaking__wrapper">
        <div class="accFeed__sidebar matchmaking__sidebar">

          <div class="search__form-container">
            <h3>Search</h3>
            <div class="search__form">
              <input
                v-model.trim="searchQuery"
                type="text"
                class="search__input"
                placeholder="Type to search"
                @input="onSearchInput"
              />
              <button type="button" class="search__form-btn">
                <span class="search__form-icon"></span>
              </button>
            </div>
          </div>

          <div class="accordion" id="accordionMarketplace">
            <div class="card sidebar">
              <div class="card-header" id="headingMarketplace">
                <button
                  class="btn-accordion"
                  type="button"
                  data-toggle="collapse"
                  data-target="#collapseMarketplace"
                  aria-expanded="true"
                  aria-controls="collapseMarketplace"
                >
                  <span>Marketplace</span>
                </button>
              </div>

              <div
                id="collapseMarketplace"
                class="collapse show"
                aria-labelledby="headingMarketplace"
                data-parent="#accordionMarketplace"
              >
                <div class="card-body">
                  <ul class="channel__list">
                    <li class="channel__item">
                      <a
                        class="channel__link"
                        :class="{ active: !marketplaceSelectedId }"
                        href="#"
                        @click.prevent="selectMarketplace(null)"
                      >
                        All my products
                      </a>
                    </li>
                    <li v-for="m in marketplaces" :key="m.id" class="channel__item">
                      <a
                        class="channel__link"
                        :class="{ active: String(marketplaceSelectedId) === String(m.id) }"
                        href="#"
                        @click.prevent="selectMarketplace(m)"
                      >
                        {{ m.name }}
                        <MarketplaceUnreadBadge :count="marketplaceBulb(m.id)" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="sidebar-block">
            <a class="link" href="/faq.html" target="_blank" rel="noopener noreferrer">FAQ</a>
          </div>
        </div>

        <div class="accFeed__content matchmaking__content">
          <template v-if="isProductsView">
            <div class="offers">
              <div class="offers-status">
                <h3>You have: <strong>{{ products.length }}</strong> products</h3>
                <h3>Marketplace: <strong>{{ marketplaceSelectedName }}</strong></h3>
                <div class="offers-status__filters">
                  <div>
                    <a
                      v-for="filter in statusFilters"
                      :key="filter.key"
                      href="#"
                      @click.prevent="statusFilter = filter.key"
                    >
                      <span
                        class="status"
                        :class="[filter.key, { active: statusFilter === filter.key }]"
                      >
                        {{ filter.label }}
                      </span>
                    </a>
                  </div>
                </div>
              </div>

              <SuccessMessage v-if="successText" :text="successText" />

              <div v-if="!isLoading && !filteredProducts.length" class="no-content">
                <p>
                  You have <strong>no products</strong>
                  <span v-if="marketplaceSelectedId"> in this marketplace.</span>
                </p>
              </div>
              <div class="offers__list accordion offers__box" id="accordionOffers">
                <SellerProductCard
                  v-for="product in filteredProducts"
                  :key="product.id"
                  :product="product"
                  :statusResolver="getProductStatus"
                  @view="openProductDetails"
                  @edit="openEditProductModal"
                  @finalize="openEditProductModal"
                  @approve="openCounterOfferApproveModal"
                />
              </div>
              <BModal
                id="modal-product-form"
                ref="productModal"
                data-id="modal-request-project"
                size="xl"
                hide-header
                hide-footer
                no-close-on-backdrop
                @hidden="onProductModalHidden"
              >
                <ProductFormRequest
                  :marketplaces="marketplaces"
                  :marketplaceId="marketplaceSelectedId"
                  :marketplaceName="marketplaceSelectedName"
                  :countries="countries"
                  :fields="productFields"
                  :formId="formId"
                  :isNewProduct="isNewProduct"
                  :product="editingProduct"
                  @closeModal="closeProductModal"
                  @onCreateProduct="onProductSaved('created')"
                  @onUpdateProduct="onProductSaved('updated')"
                  @onDeleteProduct="onProductSaved('deleted')"
                />
              </BModal>
              <BModal
                ref="approveCounterOfferModal"
                id="modal-approve-counter-offer"
                class="modal-request-offer"
                size="xs"
                centered
                hide-header
                hide-footer
                @hidden="onApproveCounterOfferModalHidden"
              >
                <div class="body">
                  <ConfirmMessage
                    text="Are you sure you'd like to approve this counteroffer?"
                    :okDisabled="approvingCounterOffer"
                    :cancelDisabled="approvingCounterOffer"
                    @ok="onCounterOfferApproved"
                    @cancel="closeCounterOfferApproveModal"
                  />
                </div>
              </BModal>
            </div>
          </template>

          <template v-else>
            <div class="offers">
              <div class="offers-status">
                <h3>You have: <strong>
                  {{ filteredSavedProjects.length }}
                  </strong> saved project
                  <span v-if="filteredSavedProjects.length !== 1">s</span>
                </h3>
                <h3>Marketplace: <strong>{{ marketplaceSelectedName }}</strong></h3>
                <div class="offers-status__filters">
                  <div>
                    <a
                      v-for="filter in projectStatusFilters"
                      :key="filter.key"
                      href="#"
                      @click.prevent="savedStatusFilter = filter.key"
                    >
                      <span
                        class="status"
                        :class="[filter.className || filter.key, { active: savedStatusFilter === filter.key }]"
                      >
                        {{ filter.label }}
                      </span>
                    </a>
                  </div>
                </div>
              </div>

              <h5>My saved projects</h5>

              <div v-if="!savedLoading && !filteredSavedProjects.length" class="no-content">
                <p>You have <strong>no saved projects</strong> yet.</p>
              </div>

              <div v-for="item in filteredSavedProjects"
                   :key="`${item.__itemType || 'project'}-${item.id}`" class="mb-3">
                <SavedProject
                  :data="item"
                  :item-type="item.__itemType || 'project'"
                  @getProjects="fetchSavedProjects"
                />
              </div>

              <div v-if="savedLoading" class="d-flex justify-content-center p-2">
                <div class="loader"></div>
              </div>
            </div>
          </template>

          <div v-if="isLoading && isProductsView" class="d-flex justify-content-center p-2">
            <div class="loader"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BModal } from 'bootstrap-vue';
import MatchmakingService from '@/services/MatchmakingService';
import DictionaryService from '@/services/DictionaryService';
import ProductFormRequest from '@/components/matchmaking/ProductFormRequest.vue';
import SellerProductCard from '@/components/matchmaking/SellerProductCard.vue';
import SuccessMessage from '@/components/matchmaking/SuccessMessage.vue';
import ConfirmMessage from '@/components/matchmaking/ConfirmMessage.vue';
import MarketplaceUnreadBadge from '@/components/matchmaking/MarketplaceUnreadBadge.vue';
import SavedProject from './SavedProject.vue';

export default {
  name: 'SellerOffersPage',
  components: {
    BModal,
    SellerProductCard,
    ProductFormRequest,
    SavedProject,
    SuccessMessage,
    ConfirmMessage,
    MarketplaceUnreadBadge,
  },
  data() {
    return {
      isLoading: false,
      savedLoading: false,
      marketplaces: [],
      marketplaceSelectedId: null,
      searchQuery: '',
      statusFilter: 'all',
      savedStatusFilter: 'saved',
      products: [],
      savedProjects: [],
      savedProjectsCount: 0,
      countries: [],
      productFields: [],
      formId: '',
      isNewProduct: true,
      editingProduct: null,
      successText: '',
      successTimer: null,
      searchUrlTimer: null,
      pendingCounterOfferId: null,
      pendingCounterOfferPayload: null,
      approvingCounterOffer: false,
    };
  },
  computed: {
    isProductsView() {
      if (this.$route.meta && this.$route.meta.sellerSavedView) return false;
      return this.$route.query.type !== 'projects';
    },

    marketplaceSelectedName() {
      if (!this.marketplaceSelectedId) return 'All';
      const found = (this.marketplaces || [])
        .find((m) => String(m.id) === String(this.marketplaceSelectedId));
      return found ? found.name : 'All';
    },

    statusFilters() {
      return [
        { key: 'all', label: 'All' },
        { key: 'offered', label: 'Offered' },
        { key: 'draft', label: 'Draft' },
        { key: 'threads', label: 'Message threads' },
        { key: 'approved', label: 'Approved' },
        { key: 'counteroffered', label: 'Counter offered' },
      ];
    },

    projectStatusFilters() {
      return [
        { key: 'all', label: 'All' },
        { key: 'offered', label: 'Offered' },
        { key: 'threads', label: 'Message threads' },
        { key: 'approved', label: 'Approved' },
      ];
    },

    statusCounts() {
      const counts = {
        draft: 0,
        approved: 0,
        counteroffered: 0,
      };

      (this.products || []).forEach((p) => {
        const status = this.getProductStatus(p);
        if (status === 'draft') counts.draft += 1;
        if (status === 'approved') counts.approved += 1;
        if (status === 'counteroffered') counts.counteroffered += 1;
      });

      return counts;
    },

    filteredProducts() {
      const list = this.products || [];
      const query = String(this.searchQuery || '').toLowerCase();

      return list
        .filter((p) => {
          if (!query) return true;
          const hay = `${p.name || ''} ${p.company_name || ''}`.toLowerCase();
          return hay.includes(query);
        })
        .filter((p) => {
          if (this.statusFilter === 'all') return true;

          const status = this.getProductStatus(p);

          if (this.statusFilter === 'offered') {
            return status === 'offered';
          }
          if (this.statusFilter === 'draft') {
            return status === 'draft';
          }
          if (this.statusFilter === 'threads') {
            return this.getProductThreadsCount(p) > 0;
          }
          if (this.statusFilter === 'approved') {
            return status === 'approved';
          }
          if (this.statusFilter === 'counteroffered') {
            const hasCounterOffers = this.getProductCounterOffersCount(p) > 0;
            const isDoneOrDeadline = status === 'approved' || Number(p.status) === 4;
            return hasCounterOffers && !isDoneOrDeadline;
          }

          return status === this.statusFilter;
        });
    },

    baseSavedProjects() {
      const list = this.savedProjects || [];
      const query = String(this.searchQuery || '').toLowerCase();

      return list.filter((p) => {
        const matchesMarketplace = !this.marketplaceSelectedId
          || (p.marketplace && String(p.marketplace.id) === String(this.marketplaceSelectedId));

        const matchesSearch = !query
          || String(p.name || '').toLowerCase().includes(query);

        return matchesMarketplace && matchesSearch;
      });
    },

    filteredSavedProjects() {
      const base = this.baseSavedProjects;

      if (this.savedStatusFilter === 'all') return base;
      if (this.savedStatusFilter === 'offered') {
        return base.filter((p) => Number(p.offers_count || 0) > 0);
      }
      if (this.savedStatusFilter === 'threads') {
        return base.filter((p) => this.getProjectThreadsCount(p) > 0);
      }
      if (this.savedStatusFilter === 'approved') {
        return base.filter((p) => this.isProjectApproved(p));
      }

      return base;
    },
  },
  watch: {
    '$route.query.create': {
      immediate: true,
      handler(val) {
        if (!val) return;
        if (!Array.isArray(this.marketplaces) || !this.marketplaces.length) return;

        this.$nextTick(() => {
          if (this.$refs.productModal) {
            this.openCreateProductModal();
          }
        });
      },
    },
    '$route.query.marketplace': {
      immediate: true,
      handler(val) {
        if (val === undefined || val === null || val === '') return;
        const fromQuery = String(val);
        if (String(this.marketplaceSelectedId || '') === fromQuery) return;

        const exists = (this.marketplaces || [])
          .some((m) => String(m?.id) === fromQuery);
        if (exists) this.marketplaceSelectedId = fromQuery;
      },
    },
    marketplaceSelectedId() {
      if (this.isProductsView) this.fetchProducts();
    },

    isProductsView: {
      immediate: true,
      handler(isProductsView) {
        if (isProductsView) {
          this.fetchProducts();
        } else {
          this.savedStatusFilter = 'all';
          this.fetchSavedProjects();
        }
      },
    },
    '$route.query.search': {
      immediate: true,
      handler(val) {
        const q = String(val || '').trim();
        if (this.searchQuery !== q) {
          this.searchQuery = q;
        }
      },
    },
  },
  async created() {
    const searchFromRoute = this.$route.query.search;
    if (searchFromRoute != null && searchFromRoute !== '') {
      this.searchQuery = String(searchFromRoute).trim();
    }
    await this.fetchMarketplaces();
    const marketplaceFromQuery = this.$route.query.marketplace;
    if (marketplaceFromQuery !== undefined && marketplaceFromQuery !== null && marketplaceFromQuery !== '') {
      const fromQuery = String(marketplaceFromQuery);
      const exists = (this.marketplaces || [])
        .some((m) => String(m?.id) === fromQuery);
      if (exists) this.marketplaceSelectedId = fromQuery;
    }
    if (this.$route.query.create) {
      this.$nextTick(() => {
        if (this.$refs.productModal) this.openCreateProductModal();
      });
    }
    await this.fetchCountries();
  },
  beforeDestroy() {
    if (this.successTimer) clearTimeout(this.successTimer);
    if (this.searchUrlTimer) clearTimeout(this.searchUrlTimer);
  },
  methods: {
    marketplaceBulb(marketplaceId) {
      return this.$store.getters.marketplaceBulb('seller', marketplaceId);
    },
    showSuccess(text) {
      this.successText = text;
      if (this.successTimer) clearTimeout(this.successTimer);
      this.successTimer = setTimeout(() => {
        this.successText = '';
        this.successTimer = null;
      }, 4000);
    },
    onSearchInput() {
      if (this.searchUrlTimer) clearTimeout(this.searchUrlTimer);
      this.searchUrlTimer = setTimeout(() => {
        this.searchUrlTimer = null;
        const q = String(this.searchQuery || '').trim();
        const current = String(this.$route.query.search || '').trim();
        if (q === current) return;

        const query = { ...this.$route.query };
        if (q) {
          query.search = q;
        } else {
          delete query.search;
        }
        this.$router.replace({ path: '/profile/matchmaking/seller', query }).catch(() => {});
      }, 300);
    },
    syncMarketplaceQuery() {
      const current = String(this.$route.query.marketplace || '');
      const next = this.marketplaceSelectedId ? String(this.marketplaceSelectedId) : '';
      if (current === next) return;

      const query = { ...this.$route.query };
      if (next) {
        query.marketplace = next;
      } else {
        delete query.marketplace;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    selectMarketplace(marketplace) {
      this.marketplaceSelectedId = marketplace && marketplace.id ? marketplace.id : null;
      this.syncMarketplaceQuery();
    },
    async fetchMarketplaces() {
      try {
        const { data } = await MatchmakingService.marketplaces();
        this.marketplaces = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('fetchMarketplaces error', e);
        this.marketplaces = [];
      }
    },

    async fetchCountries() {
      try {
        if (Array.isArray(this.marketplaces) && this.marketplaces.length) {
          const withCountries = this.marketplaces.find(
            (m) => m && m.filters
              && Array.isArray(m.filters.countries) && m.filters.countries.length,
          );
          if (withCountries) {
            this.countries = withCountries.filters.countries;
            return;
          }
        }

        const { data } = await DictionaryService.countries();
        this.countries = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('fetchCountries error', e);
        this.countries = [];
      }
    },

    async fetchProducts() {
      this.isLoading = true;
      try {
        const query = {
          user: 'seller',
        };

        if (this.marketplaceSelectedId) {
          query.marketplace_id = this.marketplaceSelectedId;
        }

        const response = await MatchmakingService.marketplaceProducts(query);
        const payload = response && response.data ? response.data : [];
        // eslint-disable-next-line no-nested-ternary
        this.products = Array.isArray(payload)
          ? payload
          : (payload && Array.isArray(payload.data) ? payload.data : []);
      } catch (e) {
        this.products = [];
      } finally {
        this.isLoading = false;
      }
    },

    async loadMarketplaceProductFields(marketplaceId) {
      this.productFields = [];
      if (!marketplaceId) return;

      try {
        const response = await MatchmakingService
          .marketplaceProducts({ marketplace_id: marketplaceId });
        const payload = response && response.data ? response.data : [];
        // eslint-disable-next-line no-nested-ternary
        const list = Array.isArray(payload)
          ? payload
          : (payload && Array.isArray(payload.data) ? payload.data : []);

        const template = (list || []).find((p) => p
          && Array.isArray(p.data)
          && p.data.length
          && p.data[0]
          && (p.data[0].id || p.data[0].name));

        this.productFields = template && Array.isArray(template.data)
          ? template.data.map((f) => ({ ...f }))
          : [];
      } catch (e) {
        this.productFields = [];
      }
    },

    async fetchSavedProjects() {
      this.savedLoading = true;
      try {
        const response = await MatchmakingService.marketplacesStatistics(false);
        const data = response.data || {};
        const projects = [];
        let count = 0;

        if (data && Array.isArray(data.projects)) {
          projects.push(...data.projects.map((p) => ({ ...p, __itemType: 'project' })));
          count = data.projects.length;
        }

        if (!projects.length && Array.isArray(data)) {
          data.forEach((m) => {
            if (m && Array.isArray(m.saved_projects)) {
              projects.push(...m.saved_projects.map((p) => ({ ...p, __itemType: 'project' })));
            }
            if (m && Array.isArray(m.saved_products)) {
              projects.push(...m.saved_products.map((p) => ({ ...p, __itemType: 'product' })));
            }
            if (m && m.saved_projects_count) count += Number(m.saved_projects_count) || 0;
            if (m && m.saved_products_count) count += Number(m.saved_products_count) || 0;
          });
        }

        if (!count && projects.length) count = projects.length;

        this.savedProjects = projects;
        this.savedProjectsCount = count;
      } catch (e) {
        this.savedProjects = [];
        this.savedProjectsCount = 0;
      } finally {
        this.savedLoading = false;
      }
    },

    openCreateProductModal() {
      this.isNewProduct = true;
      this.editingProduct = null;
      this.formId = Math.random().toString(36).slice(2);

      // eslint-disable-next-line no-unused-expressions
      this.$refs.productModal && this.$refs.productModal.show();
    },

    async openEditProductModal(product) {
      const mpId = product && (product.marketplace_id
        || (product.marketplace && product.marketplace.id));
      if (!this.marketplaceSelectedId && mpId) {
        this.marketplaceSelectedId = mpId;
      }

      this.isNewProduct = false;
      this.editingProduct = product;
      this.formId = String(product && product.id
        ? product.id : Math.random().toString(36).slice(2));

      const marketplaceId = mpId || this.marketplaceSelectedId;
      await this.loadMarketplaceProductFields(marketplaceId);

      // eslint-disable-next-line no-unused-expressions
      this.$refs.productModal && this.$refs.productModal.show();
    },

    openProductDetails(product) {
      const id = product && product.id ? String(product.id) : '';
      if (!id) return;

      this.$router.push({
        name: 'profileMatchmakingSellerProductDetails',
        params: { id },
      }).catch(() => {});
    },

    closeProductModal() {
      // eslint-disable-next-line no-unused-expressions
      this.$refs.productModal && this.$refs.productModal.hide();
    },

    onProductModalHidden() {
      this.formId = '';
      this.isNewProduct = true;
      this.editingProduct = null;

      if (this.$route.query && (this.$route.query.create || this.$route.query.marketplace)) {
        const query = { ...this.$route.query };
        delete query.create;
        delete query.marketplace;
        this.$router.replace({ query }).catch(() => {});
      }
    },

    onProductSaved(kind) {
      if (kind === 'created') this.showSuccess('Product was created');
      if (kind === 'updated') this.showSuccess('Product was updated');
      if (kind === 'deleted') this.showSuccess('Product was deleted');
      this.closeProductModal();
      this.fetchProducts();
    },

    openCounterOfferApproveModal({ offer }) {
      const offerId = offer?.id ?? offer?.offer_id;
      if (!offerId) return;
      this.pendingCounterOfferId = String(offerId);
      this.pendingCounterOfferPayload = { offer };
      if (this.$refs.approveCounterOfferModal) this.$refs.approveCounterOfferModal.show();
    },

    closeCounterOfferApproveModal() {
      if (this.approvingCounterOffer) return;
      if (this.$refs.approveCounterOfferModal) this.$refs.approveCounterOfferModal.hide();
    },

    onApproveCounterOfferModalHidden() {
      this.pendingCounterOfferId = null;
      this.pendingCounterOfferPayload = null;
      this.approvingCounterOffer = false;
    },

    cleanupActionPanels() {
      if (!this.$el) return;

      const visiblePanels = this.$el.querySelectorAll('.action-panel.is-visible');
      visiblePanels.forEach((panel) => panel.classList.remove('is-visible'));

      const activeToggles = this.$el.querySelectorAll('[data-toggle-panel].is-active');
      activeToggles.forEach((toggle) => toggle.classList.remove('is-active'));
    },

    resolveCounterOfferId(payload) {
      const direct = payload?.offer_id ?? payload?.id;
      if (direct) return String(direct);

      const nested = payload?.offer?.offer_id ?? payload?.offer?.id;
      if (nested) return String(nested);

      return '';
    },

    async onCounterOfferApproved() {
      const offerId = this.resolveCounterOfferId(this.pendingCounterOfferPayload)
        || String(this.pendingCounterOfferId || '');
      if (!offerId) {
        this.showSuccess('Failed to approve');
        return;
      }
      this.approvingCounterOffer = true;

      try {
        await MatchmakingService.changeProductOfferStatus({
          offer_id: offerId,
          value: 3,
        });
        this.showSuccess('Counter offer approved');
        if (this.$refs.approveCounterOfferModal) this.$refs.approveCounterOfferModal.hide();
        await this.fetchProducts();
        await this.$nextTick();
        this.cleanupActionPanels();
      } catch (e) {
        this.showSuccess('Failed to approve');
        if (this.$refs.approveCounterOfferModal) this.$refs.approveCounterOfferModal.hide();
        // eslint-disable-next-line no-console
        console.error('Approve counter-offer failed:', e);
      } finally {
        this.approvingCounterOffer = false;
      }
    },

    getProductStatus(product) {
      if (!product) return 'published';

      const statusName = String(product.status_name || product.statusName || '').toLowerCase();
      if (statusName) {
        if (statusName.includes('draft')) return 'draft';
        if (statusName.includes('approved')) return 'approved';
        if (statusName.includes('declined')) return 'declined';
      }

      const statusNum = Number(product.status);
      if (statusNum === 3) return 'draft';
      if (statusNum === 2) return 'approved';
      if (statusNum === 4) return 'declined';

      if (Number(product.counter_offers_count || 0) > 0) {
        return 'counteroffered';
      }
      if (Array.isArray(product.offers) && product.offers.length > 0) {
        return 'counteroffered';
      }

      if (Number(product.message_threads_count || product.messages_count || 0) > 0) {
        return 'threads';
      }
      if (Array.isArray(product.messages) && product.messages.length > 0) {
        return 'threads';
      }

      if (statusNum === 1 || statusNum === 5) return 'offered';

      return 'offered';
    },

    getItemStatus(item) {
      if (!item) return 'published';
      // eslint-disable-next-line no-underscore-dangle
      if (item.__itemType === 'product') return this.getProductStatus(item);
      return this.getProductStatus(item);
    },

    normalizeNumber(value) {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    },

    isProjectSaved(project) {
      if (!project) return false;
      if (project.saved === false || project.is_saved === false
        || project.isSaved === false) return false;
      return true;
    },

    isProjectApproved(project) {
      if (!project) return false;

      const statusNum = this.normalizeNumber(project.status);
      if (statusNum === 2) return true;

      const statusText = String(project.status_name || project.statusName || '').toLowerCase();
      if (statusText === 'approved') return true;

      if (project.approved === true
        || project.is_approved === true || project.isApproved === true) return true;

      return false;
    },

    getProjectThreadsCount(project) {
      if (!project) return 0;

      const messagesLen = Array.isArray(project.messages) ? project.messages.length : 0;
      return Math.max(
        messagesLen,
        this.normalizeNumber(project.messages_count),
        this.normalizeNumber(project.message_threads_count),
        this.normalizeNumber(project.threads_count),
        this.normalizeNumber(project.unread_messages_count),
      );
    },

    getProjectCounterOffersCount(project) {
      if (!project) return 0;

      const candidates = [
        project.counter_offers_count,
        project.counteroffers_count,
        project.counterOffersCount,
      ];
      const raw = candidates.find((v) => v !== undefined && v !== null);
      return this.normalizeNumber(raw);
    },

    getProductThreadsCount(product) {
      if (!product) return 0;
      const messagesLen = Array.isArray(product.messages) ? product.messages.length : 0;
      return Math.max(
        messagesLen,
        this.normalizeNumber(product.messages_count),
        this.normalizeNumber(product.message_threads_count),
      );
    },

    getProductCounterOffersCount(product) {
      if (!product) return 0;
      const count = product.counter_offers_count ?? product.counteroffers_count ?? product.offers_count;
      if (count !== undefined && count !== null) return this.normalizeNumber(count);
      return Array.isArray(product.offers) ? product.offers.length : 0;
    },
  },
};
</script>

<style scoped lang="scss">
.offers-status__filters a {
  cursor: pointer;
  display: inline-block;
  margin-right: 3px;
  margin-bottom: 3px;
  text-decoration: none;
  color: inherit;
}

.offers__list .card {
  margin-bottom: 10px;
}

</style>
